import React, { useState, useMemo, useRef } from 'react';
import { useQuery } from 'react-query';
import { ArrowLeft, Target, BarChart3, TrendingUp, MapPin, Filter, Search, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const TaskStatus = () => {
  const [selectedCell, setSelectedCell] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const chartRef = useRef(null); // Ref for the chart container

  // Handler functions for CSP compliance
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCellChange = (e) => {
    setSelectedCell(e.target.value);
  };

  // Fetch cells for dropdown
  const { data: cells } = useQuery(
    ['cells'],
    async () => {
      const response = await axios.get('/api/masters/cells');
      return response.data;
    }
  );

  // Fetch task status data (without cell filter - for page-wide data)
  const { data: taskStatus, isLoading } = useQuery(
    ['task-status'],
    async () => {
      const response = await axios.get('/api/status/tasks');
      return response.data;
    }
  );

  // Fetch task status data for chart only (with cell filter)
  const { data: taskStatusForChart, isLoading: isLoadingChart } = useQuery(
    ['task-status-chart', selectedCell],
    async () => {
      const params = selectedCell ? { cellId: selectedCell } : {};
      const response = await axios.get('/api/status/tasks', { params });
      return response.data;
    },
    {
      enabled: true // Always enabled, but will use different data when cell is selected
    }
  );

  const getStatusColor = (status) => {
    if (!status) return 'text-gray-600 bg-gray-100';
    const statusLower = status.toLowerCase().trim().replace(/-/g, ' ');
    
    if (statusLower === 'completed') {
      return 'text-green-600 bg-green-100'; // Green #10b981
    } else if (statusLower === 'in progress') {
      return 'text-purple-600 bg-purple-100'; // Purple rgb(204, 93, 232)
    } else if (statusLower === 'on hold') {
      return 'text-orange-600 bg-orange-100'; // Orange #F99601
    } else if (statusLower === 'not started') {
      return 'text-gray-600 bg-gray-100'; // Gray #9ca3af
    }
    return 'text-gray-600 bg-gray-100';
  };

  // Task colors for legend (matching CellStatus.js)
  const getTaskColor = (taskNo) => {
    const taskColors = {
      1: '#f97316', // Orange (distinct)
      2: '#8b5cf6', // Purple (different from status purple)
      3: '#ec4899', // Pink
      4: '#06b6d4', // Cyan
      5: '#3b82f6', // Blue
      6: '#14b8a6', // Teal
      7: '#f43f5e', // Rose
      8: '#a855f7', // Violet
    };
    return taskColors[taskNo] || '#f97316'; // Default orange
  };

  const getCompletionColor = (percentage) => {
    // Green only when 100% (Completed), purple for In Progress, gray for Not Started
    if (percentage === 100) return 'text-green-600';
    if (percentage > 0) return 'text-purple-600'; // In Progress color
    return 'text-gray-600'; // Not Started
  };

  const getProgressBarColor = (percentage) => {
    // Background color for progress bar fill
    if (percentage === 100) return 'bg-green-600';
    if (percentage > 0) return 'bg-purple-600'; // In Progress color
    return 'bg-gray-600'; // Not Started
  };

  // Get status bar color for chart
  const getStatusBarColor = (status) => {
    const statusLower = status?.toLowerCase().trim().replace(/-/g, ' ');
    if (statusLower === 'completed') return '#10b981'; // Green
    if (statusLower === 'in progress') return 'rgb(204, 93, 232)'; // Purple
    if (statusLower === 'on hold') return '#F99601'; // Orange
    if (statusLower === 'not started') return '#9ca3af'; // Gray
    return '#9ca3af';
  };

  // Prepare chart data - individual subtasks as bars (show all statuses)
  // Use taskStatusForChart when cell is selected, otherwise use taskStatus
  const chartData = useMemo(() => {
    const dataSource = selectedCell ? taskStatusForChart : taskStatus;
    if (!dataSource?.individualSubtasks || dataSource.individualSubtasks.length === 0) return [];
    
    
    // Status order for Y-axis - normalize status for matching
    const getStatusValue = (status) => {
      if (!status) return 0;
      const normalized = status.toLowerCase().trim().replace(/-/g, ' ');
      
      if (normalized === 'not started') return 0;
      if (normalized === 'on hold') return 1;
      if (normalized === 'in progress') return 2;
      if (normalized === 'completed') return 3;
      return 0; // Default to "Not Started"
    };
    
    // Group subtasks by task - include ALL subtasks regardless of status
    // When "All Cells" is selected, group by subtask and collect cell information
    const taskMap = new Map();
    if (selectedCell) {
      // When a cell is selected, use simple structure
      dataSource.individualSubtasks.forEach(subtask => {
        const taskKey = `Task ${subtask.task_no}`;
        if (!taskMap.has(taskKey)) {
          taskMap.set(taskKey, []);
        }
        const statusValue = getStatusValue(subtask.status);
        taskMap.get(taskKey).push({
          subtaskId: subtask.subtask_id,
          subtaskNo: subtask.subtask_no,
          subtaskName: subtask.subtask_name,
          status: subtask.status,
          statusValue: statusValue,
          remarks: subtask.remarks || null, // Include remarks when cell is selected
          cellInfo: null // No cell breakdown when cell is selected
        });
      });
    } else {
      // When "All Cells" is selected, group by subtask and collect cell statuses
      const subtaskCellMap = new Map(); // Map: subtask_id -> { subtask info, cells: [] }
      
      dataSource.individualSubtasks.forEach(subtask => {
        const subtaskKey = subtask.subtask_id;
        if (!subtaskCellMap.has(subtaskKey)) {
          subtaskCellMap.set(subtaskKey, {
            taskNo: subtask.task_no,
            subtaskId: subtask.subtask_id,
            subtaskNo: subtask.subtask_no,
            subtaskName: subtask.subtask_name,
            cells: []
          });
        }
        
        // Add cell information - check for both cell_no and Cell_No (case variations)
        const cellNo = subtask.cell_no || subtask.Cell_No;
        const stateName = subtask.state_name || subtask.State_Name;
        
        if (cellNo && stateName) {
          subtaskCellMap.get(subtaskKey).cells.push({
            cellNo: cellNo,
            cellName: `Cell ${cellNo} (${stateName})`,
            status: subtask.status || 'Not Started'
          });
        } else if (cellNo) {
          // Fallback if only cell number is available
          subtaskCellMap.get(subtaskKey).cells.push({
            cellNo: cellNo,
            cellName: `Cell ${cellNo}`,
            status: subtask.status || 'Not Started'
          });
        }
      });
      
      // Convert to taskMap structure, using the "most common" or "highest" status for display
      subtaskCellMap.forEach((subtaskData, subtaskKey) => {
        const taskKey = `Task ${subtaskData.taskNo}`;
        if (!taskMap.has(taskKey)) {
          taskMap.set(taskKey, []);
        }
        
        // Determine display status (use highest status if multiple cells have different statuses)
        const statusCounts = {};
        subtaskData.cells.forEach(cell => {
          const status = cell.status || 'Not Started';
          statusCounts[status] = (statusCounts[status] || 0) + 1;
        });
        
        // Priority: Completed > In Progress > On Hold > Not Started
        // Show the highest status that exists across all cells
        let displayStatus = 'Not Started';
        if (statusCounts['Completed'] && statusCounts['Completed'] > 0) displayStatus = 'Completed';
        else if (statusCounts['In Progress'] && statusCounts['In Progress'] > 0) displayStatus = 'In Progress';
        else if (statusCounts['On Hold'] && statusCounts['On Hold'] > 0) displayStatus = 'On Hold';
        
        const statusValue = getStatusValue(displayStatus);
        const subtaskEntry = {
          subtaskId: subtaskData.subtaskId,
          subtaskNo: subtaskData.subtaskNo,
          subtaskName: subtaskData.subtaskName,
          status: displayStatus,
          statusValue: statusValue,
          cellInfo: subtaskData.cells // Store cell breakdown for tooltip
        };
        
        taskMap.get(taskKey).push(subtaskEntry);
      });
    }
    
    // Convert to array format for chart - flatten but keep track of task groups
    const result = [];
    const taskGroupInfo = new Map(); // Track start index, end index, and task name for each group
    
    // Ensure all tasks from 1 to 5 are represented, even if they have no subtasks
    for (let taskNo = 1; taskNo <= 5; taskNo++) {
      const taskName = `Task ${taskNo}`;
      const subtasks = taskMap.get(taskName) || [];
      
      if (subtasks.length === 0) {
        // Add a placeholder entry for tasks with no subtasks so they appear on X-axis
        const startIndex = result.length;
        result.push({
          task: taskName,
          taskNo: taskNo,
          subtaskId: `${taskNo}.0`,
          subtaskNo: 0,
          status: 'Not Started',
          statusValue: 0,
          value: 0.01, // Small value so bar is invisible but tick is rendered
          isMiddleInGroup: true, // Mark as middle so label shows
          taskGroupStart: startIndex,
          taskGroupEnd: startIndex,
          isEmpty: true,
          remarks: null, // No remarks for empty tasks
          cellInfo: null // No cell info for empty tasks
        });
        
        taskGroupInfo.set(startIndex, {
          start: startIndex,
          end: startIndex,
          taskName: taskName,
          middleIndex: startIndex
        });
      } else {
        // Process tasks that have subtasks
        const sortedSubtasks = subtasks.sort((a, b) => a.subtaskNo - b.subtaskNo);
        const startIndex = result.length;
        
        sortedSubtasks.forEach((subtask, idx) => {
          // Include all subtasks
          // For "Not Started", use a very small value (0.01) so the bar is effectively invisible
          // but the label can still be positioned and displayed
          const isMiddleInGroup = idx === Math.floor(sortedSubtasks.length / 2);
          
          result.push({
            task: taskName,
            taskNo: taskNo,
            subtaskId: subtask.subtaskId,
            subtaskNo: subtask.subtaskNo,
            subtaskName: subtask.subtaskName,
            status: subtask.status,
            statusValue: subtask.statusValue,
            // Use 0.01 for "Not Started" so bar is invisible but label can be shown
            value: subtask.statusValue === 0 ? 0.01 : Number(subtask.statusValue),
            isMiddleInGroup: isMiddleInGroup,
            taskGroupStart: startIndex,
            taskGroupEnd: startIndex + sortedSubtasks.length - 1,
            isEmpty: false,
            remarks: subtask.remarks || null, // Include remarks for tooltip
            cellInfo: subtask.cellInfo // Include cell information for tooltip
          });
        });
        
        // Store group info: [startIndex, endIndex, taskName]
        taskGroupInfo.set(startIndex, {
          start: startIndex,
          end: startIndex + sortedSubtasks.length - 1,
          taskName: taskName,
          middleIndex: startIndex + Math.floor(sortedSubtasks.length / 2)
        });
      }
    }
    
    // Store taskGroupInfo for access in component
    result._taskGroupInfo = taskGroupInfo;
    
    return result.sort((a, b) => {
      if (a.taskNo !== b.taskNo) return a.taskNo - b.taskNo;
      return a.subtaskNo - b.subtaskNo;
    });
  }, [selectedCell, taskStatus?.individualSubtasks, taskStatusForChart?.individualSubtasks]);

  // Download chart as PDF
  const downloadChartAsPDF = async () => {
    if (!chartRef.current) return;

    try {
      // Capture the chart and legends
      const canvas = await html2canvas(chartRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      
      const imgWidth = 297; // A4 width in mm (landscape)
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let yPosition = 15;
      
      // Add title
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      const title = selectedCell 
        ? `Task Subtask Status Breakdown - ${cells?.find(c => c.cell_id === selectedCell)?.cell_no ? `Cell ${cells.find(c => c.cell_id === selectedCell).cell_no} (${cells.find(c => c.cell_id === selectedCell).state_name})` : 'Selected Cell'}`
        : 'Task Subtask Status Breakdown';
      pdf.text(title, 148.5, yPosition, { align: 'center' });
      yPosition += 7;
      
      // Add date
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, 148.5, yPosition, { align: 'center' });
      yPosition += 10;

      // Add detailed task breakdown section
      if (yPosition > 160) {
        pdf.addPage('landscape', 'a4');
        yPosition = 15;
      }
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('1. Detailed Task Status Breakdown', 15, yPosition);
      yPosition += 10;

      // Get the data source
      const dataSource = selectedCell ? taskStatusForChart : taskStatus;
      
      // Group subtasks by task
      const taskMap = new Map();
      if (dataSource?.individualSubtasks && dataSource.individualSubtasks.length > 0) {
        dataSource.individualSubtasks.forEach(subtask => {
          const taskKey = `Task ${subtask.task_no}`;
          if (!taskMap.has(taskKey)) {
            taskMap.set(taskKey, {
              taskNo: subtask.task_no,
              subtasks: []
            });
          }
          taskMap.get(taskKey).subtasks.push({
            subtaskId: subtask.subtask_id,
            status: subtask.status
          });
        });
      }

      // Display task details in a table format
      const sortedTasks = Array.from(taskMap.values())
        .sort((a, b) => a.taskNo - b.taskNo);
      
      // Table header
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Task', 15, yPosition);
      pdf.text('Total', 70, yPosition);
      pdf.text('Completed', 130, yPosition);
      pdf.text('Not Started', 200, yPosition);
      yPosition += 5;
      
      // Draw header line
      pdf.setDrawColor(200, 200, 200);
      pdf.line(15, yPosition - 2, 287, yPosition - 2);
      yPosition += 3;
      
      sortedTasks.forEach(task => {
        if (yPosition > 185) {
          pdf.addPage('landscape', 'a4');
          yPosition = 15;
          // Redraw header on new page
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Task', 15, yPosition);
          pdf.text('Total', 70, yPosition);
          pdf.text('Completed', 130, yPosition);
          pdf.text('Not Started', 200, yPosition);
          yPosition += 5;
          pdf.setDrawColor(200, 200, 200);
          pdf.line(15, yPosition - 2, 287, yPosition - 2);
          yPosition += 3;
        }
        
        // Count subtasks by status - only count Completed and Not Started
        const statusCounts = {
          'Completed': 0,
          'Not Started': 0
        };
        
        task.subtasks.forEach(subtask => {
          const status = subtask.status || 'Not Started';
          if (status === 'Completed') {
            statusCounts['Completed']++;
          } else {
            // All other statuses (In Progress, On Hold, Not Started, etc.) count as Not Started
            statusCounts['Not Started']++;
          }
        });

        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Task ${task.taskNo}`, 15, yPosition);
        pdf.text(`${task.subtasks.length}`, 70, yPosition);
        pdf.text(`${statusCounts['Completed']}`, 130, yPosition);
        pdf.text(`${statusCounts['Not Started']}`, 200, yPosition);
        yPosition += 5;
      });
      
      yPosition += 5; // Extra space after table

      // Add cell-wise subtask data section in tabular format
      if (!selectedCell && taskStatus?.cellSubtaskDetails && taskStatus.cellSubtaskDetails.length > 0) {
        if (yPosition > 150) {
          pdf.addPage('landscape', 'a4');
          yPosition = 15;
        }
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('2. Cell-Wise Subtask Details', 15, yPosition);
        yPosition += 10;

        // Build a map of all subtasks with their status per cell
        const subtaskMap = new Map(); // subtask_id -> { taskNo, subtaskNo, subtaskId, cells: Map<cellKey, status> }
        const cellSet = new Set();
        
        taskStatus.cellSubtaskDetails.forEach(item => {
          const cellKey = `Cell ${item.cell_no} (${item.state_name})`;
          cellSet.add(cellKey);
          
          const subtaskKey = item.subtask_id;
          if (!subtaskMap.has(subtaskKey)) {
            subtaskMap.set(subtaskKey, {
              taskNo: item.task_no,
              subtaskNo: item.subtask_no,
              subtaskId: item.subtask_id,
              cells: new Map()
            });
          }
          
          const status = item.status || 'Not Started';
          subtaskMap.get(subtaskKey).cells.set(cellKey, status);
        });

        // Sort cells by cell number
        const sortedCells = Array.from(cellSet).sort((a, b) => {
          const numA = parseInt(a.match(/Cell (\d+)/)?.[1] || '0');
          const numB = parseInt(b.match(/Cell (\d+)/)?.[1] || '0');
          return numA - numB;
        });

        // Sort subtasks by task number, then subtask number
        const sortedSubtasks = Array.from(subtaskMap.values()).sort((a, b) => {
          if (a.taskNo !== b.taskNo) return a.taskNo - b.taskNo;
          return a.subtaskNo - b.subtaskNo;
        });

        // Calculate column widths
        const pageWidth = 297; // A4 landscape width in mm
        const leftMargin = 10;
        const rightMargin = 10;
        const availableWidth = pageWidth - leftMargin - rightMargin;
        const subtaskColWidth = 30; // Width for subtask ID column
        const cellColWidth = (availableWidth - subtaskColWidth) / sortedCells.length;
        const maxCellColWidth = 8; // Maximum width per cell column for better spacing
        const minCellColWidth = 6; // Minimum width per cell column
        const actualCellColWidth = Math.max(minCellColWidth, Math.min(cellColWidth, maxCellColWidth));
        const rowHeight = 6; // Increased row height for better spacing

        // Group subtasks by task for better organization
        const tasksMap = new Map();
        sortedSubtasks.forEach(subtask => {
          const taskKey = `Task ${subtask.taskNo}`;
          if (!tasksMap.has(taskKey)) {
            tasksMap.set(taskKey, []);
          }
          tasksMap.get(taskKey).push(subtask);
        });

        // Draw table for each task group
        Array.from(tasksMap.entries()).sort((a, b) => {
          const numA = parseInt(a[0].match(/Task (\d+)/)?.[1] || '0');
          const numB = parseInt(b[0].match(/Task (\d+)/)?.[1] || '0');
          return numA - numB;
        }).forEach(([taskKey, taskSubtasks]) => {
          // Check if we need a new page
          const estimatedRows = taskSubtasks.length + 2; // +2 for header and task title
          const estimatedHeight = estimatedRows * rowHeight;
          if (yPosition + estimatedHeight > 190) {
            pdf.addPage('landscape', 'a4');
            yPosition = 15;
          }
          
          // Task header with spacing
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          pdf.text(taskKey, leftMargin, yPosition);
          yPosition += 8;
          
          // Table header with better styling
          pdf.setFontSize(8);
            pdf.setFont('helvetica', 'bold');
          pdf.setFillColor(230, 230, 230);
          const headerHeight = rowHeight + 2;
          pdf.rect(leftMargin, yPosition - headerHeight + 2, availableWidth, headerHeight, 'F');
          
          // Draw header border
          pdf.setDrawColor(180, 180, 180);
          pdf.setLineWidth(0.5);
          pdf.rect(leftMargin, yPosition - headerHeight + 2, availableWidth, headerHeight);
          
          // Subtask column header with padding
          pdf.text('Subtask', leftMargin + 5, yPosition);
          
          // Draw vertical line after subtask column
          pdf.line(leftMargin + subtaskColWidth, yPosition - headerHeight + 2, leftMargin + subtaskColWidth, yPosition + 2);
          
          // Cell column headers
          sortedCells.forEach((cellKey, idx) => {
            const xPos = leftMargin + subtaskColWidth + (idx * actualCellColWidth);
            const cellNo = cellKey.match(/Cell (\d+)/)?.[1] || '';
            // Use just cell number for header
            const headerText = cellNo.length > 0 ? `C${cellNo}` : cellKey.substring(0, 6);
            const textWidth = pdf.getTextWidth(headerText);
            pdf.text(headerText, xPos + (actualCellColWidth / 2) - (textWidth / 2), yPosition);
            
            // Draw vertical line between cell columns
            if (idx < sortedCells.length - 1) {
              pdf.line(xPos + actualCellColWidth, yPosition - headerHeight + 2, xPos + actualCellColWidth, yPosition + 2);
            }
          });
          
          yPosition += rowHeight + 2;

          // Draw rows for each subtask
          taskSubtasks.forEach((subtask, rowIdx) => {
                if (yPosition > 185) {
                  pdf.addPage('landscape', 'a4');
                  yPosition = 15;
              // Redraw header
              pdf.setFontSize(8);
              pdf.setFont('helvetica', 'bold');
              pdf.setFillColor(230, 230, 230);
              const headerHeight = rowHeight + 2;
              pdf.rect(leftMargin, yPosition - headerHeight + 2, availableWidth, headerHeight, 'F');
              pdf.setDrawColor(180, 180, 180);
              pdf.setLineWidth(0.5);
              pdf.rect(leftMargin, yPosition - headerHeight + 2, availableWidth, headerHeight);
              pdf.text('Subtask', leftMargin + 5, yPosition);
              pdf.line(leftMargin + subtaskColWidth, yPosition - headerHeight + 2, leftMargin + subtaskColWidth, yPosition + 2);
              sortedCells.forEach((cellKey, idx) => {
                const xPos = leftMargin + subtaskColWidth + (idx * actualCellColWidth);
                const cellNo = cellKey.match(/Cell (\d+)/)?.[1] || '';
                const headerText = cellNo.length > 0 ? `C${cellNo}` : cellKey.substring(0, 6);
                const textWidth = pdf.getTextWidth(headerText);
                pdf.text(headerText, xPos + (actualCellColWidth / 2) - (textWidth / 2), yPosition);
                if (idx < sortedCells.length - 1) {
                  pdf.line(xPos + actualCellColWidth, yPosition - headerHeight + 2, xPos + actualCellColWidth, yPosition + 2);
                }
              });
              yPosition += rowHeight + 2;
            }

            // Draw cell background for alternating rows (optional - light gray every other row)
            if (rowIdx % 2 === 0) {
              pdf.setFillColor(250, 250, 250);
              pdf.rect(leftMargin, yPosition - rowHeight + 1, availableWidth, rowHeight, 'F');
            }

            // Subtask ID with padding
            pdf.setFontSize(8);
                pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(0, 0, 0);
            pdf.text(subtask.subtaskId, leftMargin + 5, yPosition);

            // Draw vertical line after subtask column
            pdf.setDrawColor(200, 200, 200);
            pdf.setLineWidth(0.3);
            pdf.line(leftMargin + subtaskColWidth, yPosition - rowHeight + 1, leftMargin + subtaskColWidth, yPosition + 1);

            // Cell status indicators
            sortedCells.forEach((cellKey, idx) => {
              const xPos = leftMargin + subtaskColWidth + (idx * actualCellColWidth);
              const centerX = xPos + (actualCellColWidth / 2);
              const status = subtask.cells.get(cellKey) || 'Not Started';
              
              if (status === 'Completed') {
                // Draw green checkmark using lines
                pdf.setDrawColor(0, 150, 0);
                pdf.setLineWidth(1.2);
                const checkSize = 2;
                const checkY = yPosition - 1;
                // Draw checkmark: bottom-left to center, then center to top-right
                pdf.line(centerX - checkSize, checkY, centerX - checkSize/3, checkY + checkSize/2);
                pdf.line(centerX - checkSize/3, checkY + checkSize/2, centerX + checkSize, checkY - checkSize);
                  } else {
                // Draw red cross using lines
                pdf.setDrawColor(200, 0, 0);
                pdf.setLineWidth(1.2);
                const crossSize = 2;
                const crossY = yPosition - 1;
                // Draw X: diagonal lines
                pdf.line(centerX - crossSize, crossY - crossSize, centerX + crossSize, crossY + crossSize);
                pdf.line(centerX + crossSize, crossY - crossSize, centerX - crossSize, crossY + crossSize);
              }
              
              // Draw vertical line between cell columns
              if (idx < sortedCells.length - 1) {
                pdf.setDrawColor(200, 200, 200);
                pdf.setLineWidth(0.3);
                pdf.line(xPos + actualCellColWidth, yPosition - rowHeight + 1, xPos + actualCellColWidth, yPosition + 1);
              }
            });

            // Reset text color, font, and draw color
            pdf.setTextColor(0, 0, 0);
            pdf.setDrawColor(0, 0, 0);

            // Draw bottom row border
            pdf.setDrawColor(200, 200, 200);
            pdf.setLineWidth(0.3);
            pdf.line(leftMargin, yPosition + 1, leftMargin + availableWidth, yPosition + 1);

            yPosition += rowHeight;
          });

          yPosition += 5; // Space between task groups
        });

        // Add legend with better spacing
        if (yPosition > 180) {
          pdf.addPage('landscape', 'a4');
          yPosition = 15;
        }
        
        yPosition += 3; // Space before legend
        
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Legend:', leftMargin, yPosition);
        yPosition += 6;
        
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        
        // Draw green checkmark for legend
        pdf.setDrawColor(0, 150, 0);
        pdf.setLineWidth(1.2);
        const legendCheckX = leftMargin + 5;
        const legendCheckY = yPosition - 1;
        const legendCheckSize = 2.5;
        pdf.line(legendCheckX - legendCheckSize, legendCheckY, legendCheckX - legendCheckSize/3, legendCheckY + legendCheckSize/2);
        pdf.line(legendCheckX - legendCheckSize/3, legendCheckY + legendCheckSize/2, legendCheckX + legendCheckSize, legendCheckY - legendCheckSize);
        pdf.setTextColor(0, 0, 0);
        pdf.text('= Completed', leftMargin + 12, yPosition);
        
        // Draw red cross for legend
        pdf.setDrawColor(200, 0, 0);
        pdf.setLineWidth(1.2);
        const legendCrossX = leftMargin + 60;
        const legendCrossY = yPosition - 1;
        const legendCrossSize = 2.5;
        pdf.line(legendCrossX - legendCrossSize, legendCrossY - legendCrossSize, legendCrossX + legendCrossSize, legendCrossY + legendCrossSize);
        pdf.line(legendCrossX + legendCrossSize, legendCrossY - legendCrossSize, legendCrossX - legendCrossSize, legendCrossY + legendCrossSize);
        pdf.setTextColor(0, 0, 0);
        pdf.text('= Not Started', leftMargin + 67, yPosition);
      }
      
      // Add chart image on new page
      pdf.addPage('landscape', 'a4');
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('3. Visual Chart', 148.5, 15, { align: 'center' });
      
      // Add chart image
      const chartY = 25;
      const chartHeight = Math.min(imgHeight - 20, 150); // Limit height to fit on page
      pdf.addImage(imgData, 'PNG', 10, chartY, imgWidth - 20, chartHeight);
      
      // Save PDF
      const fileName = selectedCell 
        ? `Task_Status_Report_Cell_${cells?.find(c => c.cell_id === selectedCell)?.cell_no || 'Selected'}_${new Date().toISOString().split('T')[0]}.pdf`
        : `Task_Status_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      const subtaskId = data?.subtaskId;
      const subtaskName = data?.subtaskName;
      const status = data?.status;
      const remarks = data?.remarks;
      const cellInfo = data?.cellInfo;
      
      return (
        <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg max-w-md z-50">
          <p className="font-semibold mb-2 text-gray-900">
            Subtask {subtaskId}{subtaskName ? `: ${subtaskName}` : ''}
          </p>
          <p className="text-sm mb-2 dynamic-text-color" style={{ '--dynamic-text-color': getStatusBarColor(status) }}>
            Status: <span className="font-semibold">{status}</span>
          </p>
          
          {/* Show remarks when a cell is selected */}
          {selectedCell && remarks && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <p className="text-xs font-semibold text-gray-700 mb-1">Remarks:</p>
              <p className="text-xs text-gray-600">{remarks}</p>
            </div>
          )}
          
          {!selectedCell && cellInfo && Array.isArray(cellInfo) && cellInfo.length > 0 && (() => {
            const completedCells = cellInfo.filter(cell => {
              const cellStatus = cell?.status || 'Not Started';
              return cellStatus === 'Completed';
            });
            
            if (completedCells.length === 0) return null;
            
            return (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2">Completed in:</p>
                <div className="text-xs">
                  <span className="text-gray-700">
                    {completedCells.map(cell => cell?.cellName || `Cell ${cell?.cellNo}`).join(', ')}
                  </span>
                </div>
              </div>
            );
          })()}
          
          {!selectedCell && (!cellInfo || cellInfo.length === 0) && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <p className="text-xs text-gray-500 italic">Cell breakdown not available</p>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Task Wise Status</h1>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <label className="text-sm font-medium text-gray-700">Status:</label>
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="Completed">Completed</option>
              <option value="In Progress">In Progress</option>
              <option value="On Hold">On Hold</option>
              <option value="Not Started">Not Started</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <Search className="h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search tasks or cells..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <>
          {/* Task Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {taskStatus?.taskOverview?.map((task) => (
              <div key={task.task_id} className="card">
                <div className="flex items-center justify-between mb-4">
                  <Target className="h-8 w-8 text-primary-600" />
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.overall_status)}`}>
                    {task.overall_status}
                  </span>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  <span style={{ color: getTaskColor(task.task_no), fontWeight: '600' }}>Task {task.task_no}</span>: {task.task_name}
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Overall Progress</span>
                    <span className={`font-medium ${getCompletionColor(task.overall_progress)}`}>
                      {task.overall_progress}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getProgressBarColor(task.overall_progress)}`}
                      style={{ width: `${task.overall_progress}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {task.active_cells} cells active
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Task Subtask Status Breakdown Chart */}
          {((selectedCell ? taskStatusForChart : taskStatus)?.individualSubtasks?.length > 0 || chartData.length > 0) && (
            <div className="card">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center whitespace-nowrap">
                  <BarChart3 className="h-5 w-5 mr-2 flex-shrink-0" />
                  <span>Task Subtask Status Breakdown</span>
                </h3>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <button
                    onClick={downloadChartAsPDF}
                    className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download PDF</span>
                  </button>
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4 text-gray-500 flex-shrink-0" />
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Cell:</label>
                    <select
                      value={selectedCell}
                      onChange={handleCellChange}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent min-w-[200px] w-full sm:w-auto"
                      disabled={!cells || isLoadingChart}
                    >
                      <option value="">All Cells</option>
                      {cells && cells.length > 0 ? (
                        cells.map((cell) => (
                          <option key={cell.cell_id} value={cell.cell_id}>
                            Cell {cell.cell_no} ({cell.state_name})
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>Loading cells...</option>
                      )}
                    </select>
                  </div>
                </div>
              </div>
              
              {isLoadingChart && selectedCell ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
              ) : chartData.length > 0 ? (
                <div ref={chartRef}>
              {/* Status and Task Legends */}
              <div className="mb-6 pb-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white rounded-lg p-5">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Status Colors Legend */}
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center mb-3">
                      <div className="w-1 h-5 bg-gradient-to-b from-green-500 to-emerald-500 rounded-full mr-2"></div>
                      <p className="text-sm font-bold text-gray-800">Status</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 transition-colors">
                        <div className="w-5 h-5 rounded-full shadow-md border-2 border-white dynamic-bg-color" style={{ '--dynamic-bg-color': '#10b981' }}></div>
                        <span className="text-sm font-semibold text-gray-700">Completed</span>
                      </div>
                      <div className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 transition-colors">
                        <div className="w-5 h-5 rounded-full shadow-md border-2 border-white dynamic-bg-color" style={{ '--dynamic-bg-color': 'rgb(204, 93, 232)' }}></div>
                        <span className="text-sm font-semibold text-gray-700">In Progress</span>
                      </div>
                      <div className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 transition-colors">
                        <div className="w-5 h-5 rounded-full shadow-md border-2 border-white dynamic-bg-color" style={{ '--dynamic-bg-color': '#F99601' }}></div>
                        <span className="text-sm font-semibold text-gray-700">On Hold</span>
                      </div>
                      <div className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 transition-colors">
                        <div className="w-5 h-5 rounded-full shadow-md border-2 border-white dynamic-bg-color" style={{ '--dynamic-bg-color': '#9ca3af' }}></div>
                        <span className="text-sm font-semibold text-gray-700">Not Started</span>
                      </div>
                    </div>
                  </div>

                  {/* Task Colors Legend */}
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center mb-3">
                      <div className="w-1 h-5 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full mr-2"></div>
                      <p className="text-sm font-bold text-gray-800">Tasks</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {(() => {
                        // Show all tasks from 1 to 5 (or up to 8 if needed)
                        const allTasks = [];
                        for (let i = 1; i <= 5; i++) {
                          allTasks.push(`Task ${i}`);
                        }
                        return allTasks.map((taskName) => {
                          const taskNo = parseInt(taskName.replace('Task ', ''));
                          const baseColor = getTaskColor(taskNo);
                          return (
                            <div key={taskName} className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 transition-colors">
                              <div 
                                className="w-5 h-5 rounded shadow-md border-2 border-white dynamic-bg-color" 
                                style={{ '--dynamic-bg-color': baseColor }}
                              ></div>
                              <span className="text-sm font-semibold text-gray-700">{taskName}</span>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              <ResponsiveContainer width="100%" height={500}>
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 100, bottom: 80 }}
                  barCategoryGap="15%"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="task" 
                    type="category"
                    label={{ value: 'Tasks', position: 'insideBottom', offset: -15, style: { textAnchor: 'middle', fill: '#374151', fontWeight: 'bold', fontSize: '14px' } }}
                    tick={(props) => {
                      const { x, y, payload, index } = props;
                      if (index === undefined || !chartData || index >= chartData.length) {
                        return null;
                      }
                      const dataPoint = chartData[index];
                      if (!dataPoint) {
                        return null;
                      }
                      
                      // Only show task name at the middle position of each task group
                      // This ensures only one occurrence per task
                      const shouldShow = dataPoint?.isMiddleInGroup === true;
                      
                      // Get task color for the task name
                      const taskColor = shouldShow ? getTaskColor(dataPoint.taskNo) : 'transparent';
                      
                      return (
                        <g transform={`translate(${x},${y})`}>
                          <text
                            x={0}
                            y={0}
                            dy={16}
                            textAnchor="middle"
                            fill={taskColor}
                            fontSize={12}
                            fontWeight="500"
                          >
                            {payload.value}
                          </text>
                        </g>
                      );
                    }}
                    interval={0}
                  />
                  <YAxis 
                    type="number"
                    domain={[0, 3.5]}
                    width={100}
                    ticks={[0, 1, 2, 3]}
                    label={{ value: 'Status', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#374151', fontWeight: 'bold', fontSize: '14px' } }}
                    tick={(props) => {
                      const { x, y, payload } = props;
                      const statusMap = { 0: 'Not Started', 1: 'On Hold', 2: 'In Progress', 3: 'Completed' };
                      const status = statusMap[payload.value];
                      const statusColor = getStatusBarColor(status);
                      return (
                        <g transform={`translate(${x},${y})`}>
                          <text
                            x={0}
                            y={0}
                            dy={4}
                            textAnchor="end"
                            fill={statusColor}
                            fontSize={12}
                            fontWeight="500"
                          >
                            {status}
                          </text>
                        </g>
                      );
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="value" 
                    barSize={20}
                    barGap={4}
                  >
                    {chartData.map((entry, index) => {
                      // Skip rendering for empty placeholder entries
                      if (entry.isEmpty) {
                        return <Cell key={`cell-${index}`} fill="transparent" />;
                      }
                      
                      // Use task color for the entire bar
                      // For "Not Started" (value 0.01), make it transparent so bar is invisible
                      const taskColor = getTaskColor(entry.taskNo);
                      const isNotStarted = entry.statusValue === 0;
                      
                      return (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={isNotStarted ? 'transparent' : taskColor}
                        />
                      );
                    })}
                    <LabelList 
                      dataKey="subtaskId" 
                      position="top"
                      style={{ fontSize: '10px', fill: '#374151', fontWeight: '500' }}
                      formatter={(value, entry) => {
                        // Don't show label for empty placeholder entries
                        if (entry?.isEmpty || !value) return '';
                        return value;
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No data available for selected cell
                </div>
              )}
            </div>
          )}

          {/* Detailed Task Status by Cell - Tabular Format */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              Task Status by Cell
            </h3>
            {!taskStatus?.cellTaskStatus || taskStatus.cellTaskStatus.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No data available
              </div>
            ) : (
            <div className="overflow-x-auto">
                  {(() => {
                  // Group data by cell
                  const cellMap = new Map();
                  const taskNumbers = new Set();
                  
                  (taskStatus?.cellTaskStatus || []).forEach(item => {
                    const cellKey = `Cell ${item.cell_no} (${item.state_name})`;
                    if (!cellMap.has(cellKey)) {
                      cellMap.set(cellKey, {
                        cellNo: item.cell_no,
                        cellName: cellKey,
                        stateName: item.state_name,
                        tasks: new Map()
                      });
                    }
                    taskNumbers.add(item.task_no);
                    cellMap.get(cellKey).tasks.set(item.task_no, {
                      status: item.status,
                      progress: item.progress || 0,
                      taskName: item.task_name
                    });
                  });

                  // Sort cells by cell number
                  const sortedCells = Array.from(cellMap.values()).sort((a, b) => a.cellNo - b.cellNo);
                  const sortedTaskNos = Array.from(taskNumbers).sort((a, b) => a - b);

                  // Filter cells based on search and status
                  const filteredCells = sortedCells.filter(cell => {
                    // Search filter
                      const matchesSearch = !searchTerm || 
                      cell.cellName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      cell.stateName.toLowerCase().includes(searchTerm.toLowerCase());
                    
                    if (!matchesSearch) return false;
                    
                    // Status filter - check if any task in this cell matches the status filter
                    if (!statusFilter) return true;
                    
                    return Array.from(cell.tasks.values()).some(task => task.status === statusFilter);
                  });

                  if (sortedTaskNos.length === 0) {
                    return (
                      <div className="text-center py-8 text-gray-500">
                        No task data available
                      </div>
                    );
                  }

                  return (
                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                      <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                        <thead>
                          <tr className="bg-gray-100 border-b-2 border-gray-400">
                            <th 
                              className="py-3 px-4 font-bold text-gray-800 border-r-2 border-gray-400 sticky left-0 bg-gray-100 z-20 min-w-[180px] text-left"
                              style={{ borderCollapse: 'collapse' }}
                            >
                              <div className="flex items-center">
                                <MapPin className="h-4 w-4 mr-1 text-gray-600" />
                                <span>Cell / State</span>
                              </div>
                            </th>
                            {sortedTaskNos.map(taskNo => {
                              // Get task name from first cell that has this task
                              const firstCellWithTask = sortedCells.find(cell => cell.tasks.has(taskNo));
                              const taskName = firstCellWithTask?.tasks.get(taskNo)?.taskName || '';
                              
                              return (
                                <th 
                                  key={taskNo} 
                                  className="py-3 px-3 font-bold text-center min-w-[150px] border-r-2 border-gray-400"
                                  style={{ 
                                    color: getTaskColor(taskNo),
                                    borderCollapse: 'collapse'
                                  }}
                                >
                                  <div className="flex flex-col items-center">
                                    <span className="font-bold text-base">Task {taskNo}</span>
                                    {taskName && (
                                      <span className="text-xs font-normal text-gray-600 mt-1 px-2" title={taskName}>
                                        {taskName.substring(0, 25)}
                                        {taskName.length > 25 ? '...' : ''}
                                      </span>
                                    )}
                                  </div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredCells.length === 0 ? (
                            <tr>
                              <td colSpan={sortedTaskNos.length + 1} className="py-8 text-center text-gray-500 border-r-2 border-gray-400">
                                No cells match the current filters
                      </td>
                            </tr>
                          ) : (
                            filteredCells.map((cell, cellIdx) => (
                              <tr 
                                key={cell.cellName} 
                                className={`border-b border-gray-300 ${cellIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                              >
                                <td 
                                  className={`py-3 px-4 border-r-2 border-gray-400 font-semibold sticky left-0 z-10 ${cellIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                                  style={{ borderCollapse: 'collapse' }}
                                >
                                  <div className="flex flex-col">
                                    <span className="font-bold text-gray-900 text-base">Cell {cell.cellNo}</span>
                                    <span className="text-sm text-gray-600">{cell.stateName}</span>
                                  </div>
                      </td>
                                {sortedTaskNos.map(taskNo => {
                                  const taskData = cell.tasks.get(taskNo);
                                  if (!taskData) {
                                    return (
                                      <td 
                                        key={taskNo} 
                                        className="py-3 px-3 border-r-2 border-gray-400 text-center"
                                        style={{ borderCollapse: 'collapse' }}
                                      >
                                        <span className="text-gray-400 text-sm">-</span>
                      </td>
                                    );
                                  }
                                  return (
                                    <td 
                                      key={taskNo} 
                                      className="py-4 px-3 border-r-2 border-gray-400"
                                      style={{ borderCollapse: 'collapse' }}
                                    >
                                      <div className="flex flex-col items-center space-y-2">
                                        <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusColor(taskData.status)}`}>
                                          {taskData.status}
                                        </span>
                                        <div className="flex flex-col items-center w-full space-y-1">
                                          <div className="w-full max-w-[100px] bg-gray-200 rounded-full h-2.5">
                                            <div 
                                              className={`h-2.5 rounded-full ${getProgressBarColor(taskData.progress)}`}
                                              style={{ width: `${Math.min(taskData.progress, 100)}%` }}
                            ></div>
                          </div>
                                          <span className={`text-xs font-semibold ${getCompletionColor(taskData.progress)}`}>
                                            {taskData.progress}%
                          </span>
                                        </div>
                        </div>
                      </td>
                                  );
                                })}
                    </tr>
                            ))
                          )}
                </tbody>
              </table>
                    </div>
                  );
              })()}
            </div>
            )}
          </div>

          {/* Report Submitted Status by Cell */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              Report Submitted Status by Cell
            </h3>
            {!taskStatus?.cellTaskStatus || taskStatus.cellTaskStatus.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No data available
              </div>
            ) : (
              <div className="overflow-x-auto">
                {(() => {
                  // Group data by cell and check if any task has been submitted
                  const cellReportMap = new Map();
                  
                  (taskStatus?.cellTaskStatus || []).forEach(item => {
                    const cellKey = `Cell ${item.cell_no} (${item.state_name})`;
                    if (!cellReportMap.has(cellKey)) {
                      cellReportMap.set(cellKey, {
                        cellNo: item.cell_no,
                        cellName: cellKey,
                        stateName: item.state_name,
                        hasSubmitted: false
                      });
                    }
                    
                    // Check if this task has been submitted (submitted_by is not 'Unassigned' and not null)
                    const cellData = cellReportMap.get(cellKey);
                    if (item.submitted_by && item.submitted_by !== 'Unassigned') {
                      cellData.hasSubmitted = true;
                    }
                  });

                  // Sort cells by cell number
                  const sortedCells = Array.from(cellReportMap.values()).sort((a, b) => a.cellNo - b.cellNo);

                  // Filter cells based on search
                  const filteredCells = sortedCells.filter(cell => {
                    if (!searchTerm) return true;
                    return cell.cellName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           cell.stateName.toLowerCase().includes(searchTerm.toLowerCase());
                  });

                  return (
                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                      <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                        <thead>
                          <tr className="bg-gray-100 border-b-2 border-gray-400">
                            <th 
                              className="py-3 px-4 font-bold text-gray-800 border-r-2 border-gray-400 text-left min-w-[200px]"
                              style={{ borderCollapse: 'collapse' }}
                            >
                              <div className="flex items-center">
                                <MapPin className="h-4 w-4 mr-1 text-gray-600" />
                                <span>Cell / State</span>
                              </div>
                            </th>
                            <th 
                              className="py-3 px-4 font-bold text-gray-800 text-center min-w-[150px]"
                              style={{ borderCollapse: 'collapse' }}
                            >
                              Report Submitted
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredCells.length === 0 ? (
                            <tr>
                              <td colSpan={2} className="py-8 text-center text-gray-500 border-r-2 border-gray-400">
                                No cells match the current filters
                              </td>
                            </tr>
                          ) : (
                            filteredCells.map((cell, cellIdx) => (
                              <tr 
                                key={cell.cellName} 
                                className={`border-b border-gray-300 ${cellIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                              >
                                <td 
                                  className={`py-3 px-4 border-r-2 border-gray-400 font-semibold ${cellIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                                  style={{ borderCollapse: 'collapse' }}
                                >
                                  <div className="flex flex-col">
                                    <span className="font-bold text-gray-900 text-base">Cell {cell.cellNo}</span>
                                    <span className="text-sm text-gray-600">{cell.stateName}</span>
                                  </div>
                                </td>
                                <td 
                                  className="py-3 px-4 text-center"
                                  style={{ borderCollapse: 'collapse' }}
                                >
                                  {cell.hasSubmitted ? (
                                    <span className="px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                                      Yes
                                    </span>
                                  ) : (
                                    <span className="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                                      No
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Task Performance Metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Task Completion Timeline */}
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <TrendingUp className="h-5 w-5 mr-2" />
                Task Completion Timeline
              </h3>
              <div className="space-y-4">
                {taskStatus?.taskTimeline?.map((task) => (
                  <div key={task.task_id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">
                        <span style={{ color: getTaskColor(task.task_no), fontWeight: '600' }}>Task {task.task_no}</span>: {task.task_name}
                      </h4>
                      <span className={`text-sm font-medium ${getCompletionColor(task.completion_rate)}`}>
                        {task.completion_rate}% complete
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>Started: {new Date(task.start_date).toLocaleDateString()}</span>
                        <span>Target: {new Date(task.target_date).toLocaleDateString()}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${getProgressBarColor(task.completion_rate)}`}
                          style={{ width: `${task.completion_rate}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-gray-500">
                        {task.completed_subtasks} of {task.total_subtasks} subtasks completed
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Cell Performance Comparison */}
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <BarChart3 className="h-5 w-5 mr-2" />
                Cell Performance Comparison
              </h3>
              <div className="space-y-3">
                {taskStatus?.cellPerformance?.map((cell) => (
                  <div key={cell.cell_id} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                        <span className="font-medium text-gray-900">Cell {cell.cell_no}</span>
                        <span className="text-xs font-semibold text-primary-600 ml-2">({cell.state_name})</span>
                      </div>
                      <span className={`text-sm font-medium ${getCompletionColor(cell.avg_progress)}`}>
                        {cell.avg_progress}% avg
                      </span>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>Tasks: {cell.total_tasks}</span>
                        <span>Completed: {cell.completed_tasks}</span>
                        <span>In Progress: {cell.in_progress_tasks}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div 
                          className={`h-1.5 rounded-full ${getProgressBarColor(cell.avg_progress)}`}
                          style={{ width: `${cell.avg_progress}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TaskStatus;
